import { Button } from 'antd';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { isNil } from 'ramda';
import React, { FC, useEffect, useMemo, useRef } from 'react';

import { ReactComponent as Close } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_close.svg';
import { ReactComponent as Play } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_play.svg';

import { tvGuideMoreEpisodesSelected } from '@/Analytics/Segment';
import MoreEpisodesButton from '@/Components/Button/MoreEpisodesButton';
import buttonStyle from '@/Components/Button/styles.module.scss';
import LiveTag from '@/Components/LiveTag';
import ErrorMessage from '@/Components/Message';
import RecordButton from '@/Components/RecordButton';
import SlotProgress from '@/Components/SlotProgress/SlotProgress';
import useLivePlayer from '@/Hooks/useLivePlayer';
import { useChannelSubscriptions } from '@/Hooks/useSubscription';
import Preloader from '@/Layouts/containers/Preloader';
import { hooks, selectors, utils } from '@/Store';
import { formatGenres, getGenres } from '@/Utils/Genres';
import { getViewPage } from '@/Utils/PageLocation';
import navigateToPackage, { getOfferIdFromLinearViewingMethod } from '@/Utils/Puj';
import { formatRating } from '@/Utils/Rating';
import { getVideoImg } from '@/Utils/VideoImage';
import { ImageAssetType } from '@skytvnz/sky-app-store/lib/types/enums/ImageAssetType';
import { LinearChannel } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { useLocation } from 'react-router-dom';

import useSimpleModal from '@/Components/Modal/useSimpleModal';
import usePujFlag from '@/Hooks/usePujFlag';
import { useSelector } from 'react-redux';
import useAuth from '@/Hooks/useAuth';
import styles from './styles.module.scss';

interface SlotDetailsProps {
  slotStartTime?: string;
  channel?: LinearChannel;
  onClose: any;
  history: any;
  selectedChannelGroup?: string;
  weekday?: string;
  daysFromToday?: number;
}

const NUMBER_OF_GENRES_TO_DISPLAY = 1;

const useChannelData = () => {
  const auth = useAuth();
  const getChannel = useSelector(selectors.channels.getChannel);
  return (channelId?: string) => {
    if (!auth?.isAuthenticated) {
      return undefined;
    }

    if (channelId) {
      return getChannel(channelId);
    }
    return undefined;
  };
};

const SlotDetails: FC<SlotDetailsProps> = ({
  slotStartTime,
  onClose,
  channel,
  history,
  selectedChannelGroup,
  weekday,
  daysFromToday,
}) => {
  const modalElement = useRef<HTMLDivElement>(null);
  const {
    title,
    image,
    synopsis,
    isOnAir,
    isShow,
    isSport,
    isLoading,
    isLoadingError,
    brand,
    slot,
  } = hooks.useSlotDetailsData({ slotStartTime, channelId: channel?.id });
  const { openModal } = useSimpleModal();
  const location = useLocation();
  // Brand level information
  const genres = getGenres(formatGenres(brand?.primaryGenres), NUMBER_OF_GENRES_TO_DISPLAY);
  const start = dayjs(slot?.start).format('ddd, D MMM, h:mm a');
  const end = dayjs(slot?.end).format('h:mm a');
  const channelNumber = `Channel ${channel?.number}`;
  const rating = slot?.rating && formatRating(slot?.rating);

  const { isPUJEnabled } = usePujFlag();
  const { openAndPlay } = useLivePlayer();
  const requiredSubscriptions = useChannelSubscriptions()(channel?.id);
  const updatedChannelData = useChannelData()(channel?.id);
  const offerId = getOfferIdFromLinearViewingMethod(updatedChannelData);
  const onWatch = () => {
    if (isOnAir && channel) {
      if (isPUJEnabled) {
        if (requiredSubscriptions) {
          navigateToPackage({
            offerId,
            history,
            location,
            openModal,
          });
        } else {
          openAndPlay(
            channel,
            slot,
            selectedChannelGroup,
            0,
            history,
            true,
            'TV Guide Program View',
          );
        }
      } else {
        openAndPlay(
          channel,
          slot,
          selectedChannelGroup,
          0,
          history,
          undefined,
          'TV Guide Program View',
        );
      }
    }
  };

  const onMoreEpisodes = () => {
    if (isShow && brand?.id) {
      tvGuideMoreEpisodesSelected({
        content_id: brand?.id,
        content_title: utils.analytics.getSegmentSlotTitle(slot),
      });
      history.push(`/show/${brand.id}`);
    }
  };

  const subtitle = useMemo(() => {
    const meta: string[] = [];
    if (channel?.title) {
      meta.push(channel.title);
    }
    if (channelNumber) {
      meta.push(channelNumber);
    }
    if (genres) {
      meta.push(genres);
    }
    if (start && end) {
      meta.push(`${start} - ${end}`);
    }
    if (rating) {
      meta.push(rating);
    }
    return meta.join(' • ');
  }, [channel, channelNumber, end, genres, rating, start]);

  useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [modalElement, onClose]);

  return (
    <Preloader isLoading={isLoading || (isNil(brand) && !isLoadingError)}>
      <div className={styles.slotDetailsContainer} ref={modalElement} data-testid="slot-details">
        <div className={styles.slotDetails}>
          <Close className={styles.closeIcon} onClick={onClose} />
          {isLoadingError ? (
            <ErrorMessage className={styles.errorMsg} buttonText="Close" onButtonClick={onClose} />
          ) : (
            <>
              <div className={styles.poster}>
                <img
                  src={getVideoImg(ImageAssetType.ContentTileVertical, image)}
                  alt={`The poster of "${title}"`}
                />
              </div>
              <div className={styles.slotMeta}>
                <span className={styles.title} data-testid="slot-detail-title">
                  {title}
                </span>
                <div className={styles.attributes}>
                  {slot?.live && <LiveTag className={styles.liveTag} slot={slot} />}
                  {subtitle && <span>{subtitle}</span>}
                </div>
                {synopsis && (
                  <div className={styles.synopsis} data-testid="slot-details-synopsis">
                    {synopsis}
                  </div>
                )}
                {isOnAir && <SlotProgress slotStart={slot?.start} slotEnd={slot?.end} />}
                <div className={styles.controls}>
                  {isOnAir && (
                    <Button
                      type="primary"
                      className={classnames(buttonStyle.controlButton, buttonStyle.primary)}
                      onClick={onWatch}
                      data-testid="slot-details-watch-button"
                      name="Slot details watch button"
                    >
                      <Play className={buttonStyle.controlButtonIcon} />
                      <span className={buttonStyle.controlButtonSpan}>
                        {requiredSubscriptions ? 'Upgrade to Watch' : 'Watch'}
                      </span>
                    </Button>
                  )}
                  <RecordButton
                    slot={slot || null}
                    channel={channel || null}
                    requiredSubscriptions={requiredSubscriptions}
                    subscriptionAnalyticParams={{
                      categoryName: selectedChannelGroup,
                      contentId: slot?.programme?.id || '',
                      contentTitle: utils.analytics.getSegmentSlotTitle(slot),
                      contentType: utils.analytics.getContentTypeBySlot(slot),
                      channelName: utils.analytics.getSegmentSlotChannel(channel || null),
                      viewName: getViewPage(history),
                    }}
                    remoteRecordAnalyticParams={{
                      categoryName: selectedChannelGroup,
                      channelName: channel?.title,
                      contentTitle: utils.analytics.getSegmentSlotTitle(slot),
                      genres: utils.analytics.getSegmentSlotGenres(slot),
                      history,
                      weekday,
                      daysFromToday,
                      contentType: utils.analytics.getContentTypeBySlot(slot),
                    }}
                  />
                  {isShow && <MoreEpisodesButton onClick={onMoreEpisodes} isSport={isSport} />}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Preloader>
  );
};

export default SlotDetails;
